import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import luxonPlugin from "@fullcalendar/luxon3";
import type {
  DateSelectArg,
  EventClickArg,
  EventDropArg,
} from "@fullcalendar/core";
import type { EventResizeDoneArg } from "@fullcalendar/interaction";
import type { AdminApi } from "../api/client.js";
import { ApiError } from "../api/client.js";
import type { AffectedAppointment, BlockInput, ShiftInput } from "../api/types.js";
import { Card } from "../components/Card.js";
import { Select } from "../components/Select.js";
import { ErrorBanner } from "../components/ErrorBanner.js";
import { ShiftPopover, type ShiftPopoverState } from "../components/ShiftPopover.js";
import { AffectedApptsDialog } from "../components/AffectedApptsDialog.js";
import { colorForLocation, BLOCK_COLOR } from "../lib/schedule-colors.js";
import { buildScheduleEvents, parseEventId } from "../lib/schedule-events.js";
import { expandWeekly } from "../lib/schedule-repeat.js";
import { findSecondLocationConflict, scheduleErrorMessage } from "../lib/schedule-guards.js";
import {
  appointmentQueryBoundsInZone,
  clampMidnightEnd,
  isoDateInZone,
  scheduleRangeFromDatesSet,
  timeOfDayInZone,
} from "../lib/schedule-datetime.js";

const SECOND_LOCATION_REASON = "provider already at another location";

type Props = { api: AdminApi };

type Range = { fromDate: string; toDate: string };

// A schedule edit that stranded booked appointments (409
// `appointments_affected`) — the edit/delete has already been reverted
// (drag-move/resize) or refused (delete); this is what "Change anyway"
// retries with force=true.
type AffectedPayload =
  | { kind: "shift-update"; id: string; input: ShiftInput; affected: AffectedAppointment[] }
  | { kind: "shift-delete"; id: string; affected: AffectedAppointment[] };

export function SchedulePage({ api }: Props) {
  const queryClient = useQueryClient();
  const cfg = useQuery({
    queryKey: ["admin", "calendar-config"],
    queryFn: () => api.getCalendarConfig(),
  });
  const providers = useQuery({
    queryKey: ["admin", "providers"],
    queryFn: () => api.listProviders(),
  });
  const locations = useQuery({
    queryKey: ["admin", "locations"],
    queryFn: () => api.listLocations(),
  });
  const [providerId, setProviderId] = useState<string | "all">("all");
  const [range, setRange] = useState<Range | null>(null);
  const tz = cfg.data?.practice_timezone ?? "America/Denver";

  const schedule = useQuery({
    queryKey: ["admin", "schedule", providerId, range],
    enabled: !!range,
    queryFn: () =>
      api.getSchedule({
        ...range!,
        ...(providerId !== "all" ? { providerId } : {}),
      }),
  });

  // Appointments render as read-only background events so staff can see
  // which slots are already booked without exposing patient details here.
  // Bounds are derived in the practice timezone (not raw UTC-midnight
  // string slicing) so a booking late in the practice's local day — which
  // can fall on the *next* UTC calendar day in a negative-offset zone like
  // America/Denver — isn't silently excluded; see
  // appointmentQueryBoundsInZone. status: "booked" excludes cancelled
  // appointments, which have no business occupying a slot on the overlay.
  // The route has no providerId filter, so a single-provider view is
  // narrowed client-side below (see `visibleAppointments`).
  const appts = useQuery({
    queryKey: ["admin", "appointments", range, tz],
    enabled: !!range,
    queryFn: () =>
      api.listAppointments({
        ...appointmentQueryBoundsInZone(range!.fromDate, range!.toDate, tz),
        status: "booked",
      }),
  });

  const locationById = useMemo(
    () => new Map((locations.data?.locations ?? []).map((l) => [l.id, l])),
    [locations.data],
  );
  const providerById = useMemo(
    () => new Map((providers.data?.providers ?? []).map((p) => [p.id, p])),
    [providers.data],
  );

  const allView = providerId === "all";

  const visibleAppointments = useMemo(
    () =>
      providerId === "all"
        ? (appts.data ?? [])
        : (appts.data ?? []).filter((a) => a.provider_id === providerId),
    [appts.data, providerId],
  );

  const events = useMemo(
    () =>
      buildScheduleEvents({
        shifts: schedule.data?.shifts ?? [],
        blocks: schedule.data?.blocks ?? [],
        appointments: visibleAppointments,
        locationById,
        providerById,
        allView,
      }),
    [schedule.data, visibleAppointments, locationById, providerById, allView],
  );

  // --- Create/edit/delete (behaviors 1-3, 6) -------------------------------

  const [popover, setPopover] = useState<ShiftPopoverState | null>(null);
  const [popoverError, setPopoverError] = useState<string | null>(null);
  const [popoverBusy, setPopoverBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Drag-move/resize failures (behavior 4) — no popover is open when these
  // happen, so they get their own banner rather than the popover's.
  const [dragError, setDragError] = useState<string | null>(null);
  // Non-error outcome message (e.g. weekly-repeat summary with no skips).
  const [scheduleStatus, setScheduleStatus] = useState<string | null>(null);

  // Destructive-edit guard dialog (behavior 5), shared by drag-move/resize
  // and Delete.
  const [affected, setAffected] = useState<AffectedPayload | null>(null);
  const [forcing, setForcing] = useState(false);

  function invalidateSchedule() {
    void queryClient.invalidateQueries({ queryKey: ["admin", "schedule"] });
  }

  function onSelect(info: DateSelectArg) {
    // FullCalendar only fires `select` when `selectable` is true, which is
    // already gated to a single selected provider — this check is just
    // defense in depth.
    if (providerId === "all") return;
    setScheduleStatus(null); setDragError(null);
    setScheduleStatus(null);
    setPopoverError(null);
    setPopover({
      kind: "create",
      providerId,
      date: isoDateInZone(info.start, tz),
      startTime: timeOfDayInZone(info.start, tz),
      endTime: clampMidnightEnd(timeOfDayInZone(info.end, tz)),
      allDay: info.allDay,
    });
  }

  function onEventClick(info: EventClickArg) {
    const { kind, id } = parseEventId(info.event.id);
    if (kind === "shift") {
      const shift = (schedule.data?.shifts ?? []).find((s) => s.id === id);
      if (!shift) return;
      setScheduleStatus(null); setDragError(null);
      setPopoverError(null);
      setPopover({ kind: "edit-shift", shift });
    } else if (kind === "block") {
      const block = (schedule.data?.blocks ?? []).find((b) => b.id === id);
      if (!block) return;
      setScheduleStatus(null); setDragError(null);
      setPopoverError(null);
      setPopover({ kind: "edit-block", block });
    }
    // Appointments (kind "appt") are read-only background events — no-op.
  }

  // Weekly repeat (create mode only): one createShift per date, sequentially
  // (the API enforces one-physical-location-per-day), each pre-checked
  // against the loaded shifts. Failures are collected, not fatal.
  async function createRepeatedShifts(input: ShiftInput, repeatUntil: string) {
    const dates = expandWeekly(input.shift_date, repeatUntil);
    const loaded = schedule.data?.shifts ?? [];
    let created = 0;
    const skipped = new Map<string, string[]>(); // reason -> dates
    for (const date of dates) {
      let reason: string | null = null;
      const conflict = findSecondLocationConflict(loaded, locationById, {
        providerId: input.provider_id,
        date,
        locationId: input.location_id,
      });
      if (conflict) {
        reason = SECOND_LOCATION_REASON;
      } else {
        try {
          await api.createShift({ ...input, shift_date: date });
          created += 1;
        } catch (err) {
          reason =
            err instanceof ApiError && err.code === "second_location_conflict"
              ? SECOND_LOCATION_REASON
              : scheduleErrorMessage(err).replace(/\.$/, "");
        }
      }
      if (reason) skipped.set(reason, [...(skipped.get(reason) ?? []), date]);
    }
    invalidateSchedule();
    const skippedCount = [...skipped.values()].reduce((n, d) => n + d.length, 0);
    let summary = `Created ${created} ${created === 1 ? "shift" : "shifts"}.`;
    if (skippedCount > 0) {
      const parts = [...skipped].map(([r, d]) => `${d.join(", ")} (${r})`);
      summary += ` Skipped ${skippedCount}: ${parts.join("; ")}.`;
    }
    if (created === 0) {
      // Nothing saved: keep the popover open so the admin can adjust.
      setPopoverError(summary);
      return;
    }
    if (skippedCount > 0) {
      setDragError(summary);
      setScheduleStatus(null);
    } else {
      setScheduleStatus(null); setDragError(null);
      setScheduleStatus(summary);
    }
    setPopover(null);
  }

  async function handleSaveShift(input: ShiftInput, id?: string, repeatUntil?: string) {
    setPopoverBusy(true);
    setPopoverError(null);
    try {
      if (!id && repeatUntil) {
        await createRepeatedShifts(input, repeatUntil);
        return;
      }
      if (id) await api.updateShift(id, input);
      else await api.createShift(input);
      invalidateSchedule();
      setPopover(null);
    } catch (err) {
      if (id && err instanceof ApiError && err.code === "appointments_affected") {
        setPopover(null);
        setAffected({ kind: "shift-update", id, input, affected: err.affected ?? [] });
      } else {
        setPopoverError(scheduleErrorMessage(err));
      }
    } finally {
      setPopoverBusy(false);
    }
  }

  async function handleSaveBlock(input: BlockInput, id?: string) {
    setPopoverBusy(true);
    setPopoverError(null);
    try {
      if (id) await api.updateBlock(id, input);
      else await api.createBlock(input);
      invalidateSchedule();
      setPopover(null);
    } catch (err) {
      setPopoverError(scheduleErrorMessage(err));
    } finally {
      setPopoverBusy(false);
    }
  }

  async function handleDeleteShift(id: string) {
    setDeleteBusy(true);
    setPopoverError(null);
    try {
      await api.deleteShift(id);
      invalidateSchedule();
      setPopover(null);
    } catch (err) {
      if (err instanceof ApiError && err.code === "appointments_affected") {
        setPopover(null);
        setAffected({ kind: "shift-delete", id, affected: err.affected ?? [] });
      } else {
        setPopoverError(scheduleErrorMessage(err));
      }
    } finally {
      setDeleteBusy(false);
    }
  }

  async function handleDeleteBlock(id: string) {
    // deleteBlock has no force/guard on the API — a block only removes
    // availability, it never strands a booked appointment, so there's no
    // 409 appointments_affected path to handle here.
    setDeleteBusy(true);
    setPopoverError(null);
    try {
      await api.deleteBlock(id);
      invalidateSchedule();
      setPopover(null);
    } catch (err) {
      setPopoverError(scheduleErrorMessage(err));
    } finally {
      setDeleteBusy(false);
    }
  }

  function handleKeepAffected() {
    setAffected(null);
  }

  async function handleForceAffected() {
    if (!affected) return;
    setForcing(true);
    try {
      if (affected.kind === "shift-update") {
        await api.updateShift(affected.id, affected.input, true);
      } else {
        await api.deleteShift(affected.id, true);
      }
      invalidateSchedule();
      setAffected(null);
    } catch (err) {
      setScheduleStatus(null); setDragError(scheduleErrorMessage(err));
      setAffected(null);
    } finally {
      setForcing(false);
    }
  }

  // --- Drag-move / resize (behavior 4) -------------------------------------

  async function onEventMove(info: EventDropArg | EventResizeDoneArg) {
    const { kind, id } = parseEventId(info.event.id);
    const start = info.event.start;
    const end = info.event.end;
    if (kind === "appt" || !start || !end) {
      // Appointments are display:"background", non-editable — shouldn't
      // fire this at all, but revert defensively if it ever does. Same for
      // a missing start/end (FullCalendar shouldn't produce one here).
      info.revert();
      return;
    }

    const date = isoDateInZone(start, tz);
    const startTime = timeOfDayInZone(start, tz);
    const endTime = clampMidnightEnd(timeOfDayInZone(end, tz));

    if (kind === "shift") {
      const shift = (schedule.data?.shifts ?? []).find((s) => s.id === id);
      if (!shift) {
        info.revert();
        return;
      }
      // Client-side courtesy check: a drag/resize can move a shift onto a
      // day where the provider already has a *different* in-person
      // location (the API's 409 second_location_conflict is the backstop).
      const conflict = findSecondLocationConflict(
        schedule.data?.shifts ?? [],
        locationById,
        {
          providerId: shift.provider_id,
          date,
          locationId: shift.location_id,
          excludeShiftId: shift.id,
        },
      );
      if (conflict) {
        info.revert();
        setScheduleStatus(null); setDragError(`Already at ${conflict.display_name} that day`);
        return;
      }
      // A manual drag/resize always lands on a specific window, so it
      // converts an all-day shift into a timed one at the dragged-to
      // start/end — there's no "drag an all-day shift" gesture that keeps
      // it all-day.
      const input: ShiftInput = {
        provider_id: shift.provider_id,
        location_id: shift.location_id,
        shift_date: date,
        start_time: startTime,
        end_time: endTime,
        all_day: false,
      };
      try {
        await api.updateShift(shift.id, input);
        invalidateSchedule();
        setScheduleStatus(null); setDragError(null);
      } catch (err) {
        info.revert();
        if (err instanceof ApiError && err.code === "appointments_affected") {
          setAffected({ kind: "shift-update", id: shift.id, input, affected: err.affected ?? [] });
        } else {
          setScheduleStatus(null); setDragError(scheduleErrorMessage(err));
        }
      }
      return;
    }

    // Blocks: no conflict/destructive-edit rules on the API side.
    const block = (schedule.data?.blocks ?? []).find((b) => b.id === id);
    if (!block) {
      info.revert();
      return;
    }
    const input: BlockInput = {
      provider_id: block.provider_id,
      block_date: date,
      start_time: startTime,
      end_time: endTime,
      all_day: false,
      label: block.label,
    };
    try {
      await api.updateBlock(block.id, input);
      invalidateSchedule();
      setScheduleStatus(null); setDragError(null);
    } catch (err) {
      info.revert();
      setScheduleStatus(null); setDragError(scheduleErrorMessage(err));
    }
  }

  const activeLocations = (locations.data?.locations ?? []).filter(
    (l) => l.active,
  );

  const hasError =
    providers.isError || locations.isError || schedule.isError || appts.isError;

  return (
    <div className="grid gap-4">
      <Card title="Filters">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Select
            label="Provider"
            options={[
              { value: "all", label: "All providers" },
              ...(providers.data?.providers ?? []).map((p) => ({
                value: p.id,
                label: p.display_name,
              })),
            ]}
            value={providerId}
            onChange={(e) =>
              setProviderId(e.target.value as string | "all")
            }
          />
        </div>
      </Card>

      {hasError ? <ErrorBanner message="Could not load the schedule." /> : null}
      {dragError ? <ErrorBanner message={dragError} /> : null}
      {scheduleStatus ? (
        <div
          role="status"
          className="mb-4 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800"
        >
          {scheduleStatus}
        </div>
      ) : null}

      <Card title="Legend">
        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-700">
          {activeLocations.map((loc) => (
            <span key={loc.id} className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className="inline-block h-3 w-3 rounded-full"
                style={{ backgroundColor: colorForLocation(loc.slug) }}
              />
              {loc.display_name}
            </span>
          ))}
          <span className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="inline-block h-3 w-3 rounded-full"
              style={{ backgroundColor: BLOCK_COLOR }}
            />
            Blocked
          </span>
          <span className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="legend-hatch inline-block h-3 w-3 rounded-full"
            />
            Booked
          </span>
        </div>
      </Card>

      <Card>
        <FullCalendar
          plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin, luxonPlugin]}
          initialView="timeGridWeek"
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "timeGridWeek,dayGridMonth",
          }}
          timeZone={tz}
          slotMinTime="06:00"
          slotMaxTime="21:00"
          allDaySlot={false}
          selectable={providerId !== "all"}
          editable={providerId !== "all"}
          datesSet={(arg) => setRange(scheduleRangeFromDatesSet(arg.start, arg.end, tz))}
          events={events}
          select={onSelect}
          eventClick={onEventClick}
          eventDrop={onEventMove}
          eventResize={onEventMove}
        />
      </Card>

      {popover ? (
        <ShiftPopover
          state={popover}
          providers={providers.data?.providers ?? []}
          locations={locations.data?.locations ?? []}
          shifts={schedule.data?.shifts ?? []}
          locationById={locationById}
          saving={popoverBusy}
          deleting={deleteBusy}
          error={popoverError}
          onSaveShift={handleSaveShift}
          onSaveBlock={handleSaveBlock}
          onDeleteShift={handleDeleteShift}
          onDeleteBlock={handleDeleteBlock}
          onClose={() => setPopover(null)}
        />
      ) : null}

      {affected ? (
        <AffectedApptsDialog
          affected={affected.affected}
          timezone={tz}
          forcing={forcing}
          onKeep={handleKeepAffected}
          onForce={() => void handleForceAffected()}
        />
      ) : null}
    </div>
  );
}
